import { DEFAULT_LATEST } from 'constants/dateLimits';
import { MilitaryDisciplinaryProceduresItemModel } from 'forms/sections/military/disciplinary/types';
import { DateModel, LocationModel, ValidateJSModel } from 'forms/shared/types';
import { setToStartOfMonth } from 'helpers/date';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import { DeepSafePartial } from 'types/types';
import { isSafeUnitedStates } from 'helpers/locations';
import { hasYesOrNo } from 'models/validate';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import courtMartialChargeItemValidator from './charge';

const COURT_MARTIAL_TYPE_VALUES: readonly string[] = [
  'General',
  'Special',
  'Summary',
];

const DISCIPLINARY_PROCEDURE_NAME_VALUES: readonly string[] = [
  'Admonishment',
  'Article15',
  'CourtMartial',
  'Other',
  'Reprimand',
];

type Attributes = DeepSafePartial<MilitaryDisciplinaryProceduresItemModel>;

const isCourtMartial = (attributes: Attributes | undefined) => attributes?.DisciplinaryProcedureName?.value === 'CourtMartial';

const pvqMilitaryDisciplinaryProceduresItemValidator: ValidateJSModel<MilitaryDisciplinaryProceduresItemModel> = {

  CourtMartialCircumstances: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return { hasValue: true };
    }

    return {};
  },

  CourtMartialLocation: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return {
        presence: true,
        location: {
          requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
          requireCounty: isSafeUnitedStates(value),
          requireMilitaryDiplomaticInstallationName: true,
          requireStreet: false,
          requireZipCode: false,
          validator: pvqLocationModelValidator,
        }
      };
    }

    return {};
  },

  CourtMartialOffenseDate: (value, attributes, attributeName, options) => {
    if (isCourtMartial(attributes)) {
      return {
        date: {
          earliest: setToStartOfMonth(options?.applicantBirthdate) as DateModel,
          latest: DEFAULT_LATEST,
          requireDay: false,
        },
        presence: true,
      };
    }

    return {};
  },

  CourtMartialOffenseLocation: (value: DeepSafePartial<LocationModel | undefined>, attributes) => {
    if (isCourtMartial(attributes)) {
      return {
        presence: true,
        location: {
          requireIsMilitaryDiplomaticAddress: true,
          requireMilitaryDiplomaticInstallationName: true,
          requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
          requireCounty: isSafeUnitedStates(value),
          validator: pvqLocationModelValidator,
        }
      };
    }

    return {};
  },

  CourtMartialType: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return {
        hasValue: {
          validator: {
            inclusion: COURT_MARTIAL_TYPE_VALUES as string[],
          },
        },
      };
    }

    return {};
  },
  // this.props.DisciplinaryProcedureName?.value !== `CourtMartial`
  DisciplinaryDate: (value, attributes, attributeName, options) => ({
    date: {
      earliest: setToStartOfMonth(options?.applicantBirthdate) as DateModel,
      latest: DEFAULT_LATEST,
      requireDay: false,
    },
    presence: true
  }),

  DisciplinaryProcedureName: (value, attributes, attributeName, options) => {
    if (!isCourtMartial(attributes)) {
      return {
        hasValue: {
          validator: {
            inclusion: DISCIPLINARY_PROCEDURE_NAME_VALUES as string[],
          },
        },
      };
    }
    return {};
  },

  DisciplinaryProcedureOtherExplanation: (value, attributes) => {
    if (attributes?.DisciplinaryProcedureName?.value === 'Other' && !isCourtMartial(attributes)) {
      return { hasValue: true };
    }

    return {};
  },

  GivenBy: (value, attributes) => {
    if (attributes?.ReportedWithEmployment?.value === 'No'
      && attributes.GivenByNotApplicable?.applicable !== false) {
      return {
        presence: true,
        model: {
          validator: pvqNameModelValidator,
          requireMiddleName: false
        },
      };
    }

    return {};
  },

  GivenByNotApplicable: {},

  InvolvedAlcohol: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },

  InvolvedDomesticViolence: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },

  InvolvedDrugs: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },

  InvolvedExplosives: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },

  InvolvedFirearms: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },

  List: (value, attributes) => {
    if (isCourtMartial(attributes)) {
      return {
        branchCollection: {
          validator: courtMartialChargeItemValidator,
        },
        presence: true,
      };
    }

    return {};
  },

  ReportedWithEmployment: (value, attributes) => ({
    hasValue: {
      validator: hasYesOrNo
    }
  }),

  WhatHappened: (value, attributes) => {
    if (attributes?.ReportedWithEmployment?.value === 'No') {
      return { hasValue: true };
    }

    return {};
  },

  // non-PVQ fields in MilitaryDisciplinaryProceduresItemModel
  Comments: {},
  Court: {},
  Date: {},
  Name: {},
  Offenses: {},
  Outcome: {},
};

export default pvqMilitaryDisciplinaryProceduresItemValidator;
